/**
 * What every embed entry point does before the core arrives.
 *
 * Shared by the v1 loader (loader-entry.ts) and the verified bootstrap
 * (bootstrap/bootstrap-entry.ts). Both must do exactly this, synchronously and
 * in this order, before any other tag on the page runs: a tracker that has
 * already executed can never be un-executed. Only how they decide WHICH core
 * to inject differs.
 */

import { ScriptBlocker } from "../blocking/ScriptBlocker";
import { readCachedBlocklist } from "../blocking/blocklistCache";
import { DEFAULT_CDN_BASE } from "../buildConfig";
import { GoogleConsentMode } from "../gcm/GoogleConsentMode";
import { getScriptConfig, installTcfStub, type ScriptConfig } from "../scriptConfig";

export interface LoaderHandoff {
  config: ScriptConfig;
  blocker: ScriptBlocker;
  gcmInstalled: boolean;
  tcfInstalled: boolean;
  /** Set when window.PritectBeacon.showPreferences() was called before the core arrived. */
  pendingPreferences: boolean;
}

declare global {
  interface Window {
    __pbLoader?: LoaderHandoff;
  }
}

/**
 * Install consent defaults, start blocking, and publish the handoff.
 *
 * Returns null when the page has no usable embed tag, in which case nothing
 * was touched. Everything here is synchronous. Nothing may await.
 *
 * Deliberately does NOT define window.PritectBeacon. That global is the signal
 * our scanner uses to tell "the consent engine is running" from "the tag is on
 * the page but never executed", which is how a broken embed gets detected at
 * all. A stub here would report every broken site as healthy.
 */
export function startPrelude(): LoaderHandoff | null {
  const config = getScriptConfig();
  if (!config) return null;

  // 1. Denied-by-default consent signals, before any gtag can read them.
  let gcmInstalled = false;
  if (config.gcm) {
    try {
      new GoogleConsentMode(true).setDefaults();
      gcmInstalled = true;
    } catch {
      /* no-op */
    }
  }

  // 2. The TCF stub, before any vendor tag calls __tcfapi.
  let tcfInstalled = false;
  if (config.tcf) {
    try {
      installTcfStub();
      tcfInstalled = true;
    } catch {
      /* no-op */
    }
  }

  // 3. Start intercepting scripts. The scan-derived patterns cached from the
  //    last page view go in first, so trackers only the scanner knows about
  //    are held from the very first script the page inserts. The core
  //    refreshes the cache in the background.
  //
  //    The Tag Manager container is only let through before consent in
  //    Consent Mode "advanced", which the site has to ask for by name. In
  //    basic mode (data-gcm="1") it waits for consent like any tracker.
  const blocker = new ScriptBlocker({ tagManagerAllowed: gcmInstalled && config.gcmAdvanced === true });
  blocker.addPatterns(readCachedBlocklist(config.domainId).patterns);
  blocker.intercept();

  const handoff: LoaderHandoff = {
    config,
    blocker,
    gcmInstalled,
    tcfInstalled,
    pendingPreferences: false,
  };
  // Published before the core is injected so the core can read it on execute.
  window.__pbLoader = handoff;
  return handoff;
}

/**
 * Absolute URL of an object in the consent-scripts bucket.
 *
 * Resolved against the embed script's own URL rather than a hardcoded base, so
 * a customer who fronts the bundle with their own CDN or reverse proxy gets
 * everything from the same origin instead of a cross-origin request they did
 * not expect. Every published entry point (v1/… and releases/…) sits one
 * directory below the bucket root, so "../<path>" resolves correctly from any
 * of them. The fallback is the bucket of the instance this bundle was built
 * for, never a hardcoded shared project.
 */
export function resolveBucketUrl(path: string, selfSrc: string): string {
  try {
    if (selfSrc) return new URL(`../${path}`, selfSrc).href;
  } catch {
    /* fall through to the absolute base */
  }
  return `${DEFAULT_CDN_BASE}/${path}`;
}

/**
 * Inject the core, pinned by the given integrity.
 *
 * One retry, shortly after, for a transient network blip. Not a loop: repeated
 * attempts against a genuinely bad integrity hash just burn the customer's
 * bandwidth and never succeed.
 */
export function injectCore(url: string, integrity: string | null, retriesLeft = 1): void {
  const script = document.createElement("script");
  script.src = url;
  if (integrity) {
    script.integrity = integrity;
    script.crossOrigin = "anonymous";
  }
  script.async = false;

  script.onerror = () => {
    if (retriesLeft > 0) {
      setTimeout(() => injectCore(url, integrity, retriesLeft - 1), 1500);
      return;
    }
    failClosed(url, "Failed to load the Pritect Beacon consent engine.");
  };

  (document.head || document.documentElement).appendChild(script);
}

/**
 * The core could not be loaded, or could not be trusted.
 *
 * Blocked scripts stay blocked and no banner appears. The site keeps working,
 * analytics and ads simply do not fire. That is the deliberate choice: a site
 * with no trackers and no banner is compliant, a site with trackers and no
 * consent is not, and preventing the latter is the entire point of Beacon.
 *
 * The failure is loud where it can be: a pb:error event for the site owner's
 * own handlers, and the missing window.PritectBeacon means our next scan of
 * this domain raises a beacon_sri_broken drift alert.
 */
export function failClosed(src: string, cause: string, reason?: string): void {
  try {
    window.dispatchEvent(
      new CustomEvent("pb:error", {
        detail: {
          message: `${cause} Non-essential scripts remain blocked and no banner will be shown.`,
          src,
          ...(reason ? { reason } : {}),
        },
      }),
    );
  } catch {
    /* no-op */
  }
}
